import type { CreatePermissionRoleInput, GetApiUserManagementV1RolesParams } from '@/libs/api-user-mgt/generated/model';
import {
  getApiUserManagementV1Roles,
  getApiUserManagementV1RolesRoleId,
  getApiUserManagementV1RolesRoleIdDeviations,
  postApiUserManagementV1Roles
} from '@/libs/api-user-mgt/generated/permission-roles/permission-roles';
import { axiosInstance } from '@/libs/api-user-mgt/axios-instance';

import { mapPermissionWireRowToPermission } from '@/userManagement/application/mappers/permissionMapper';
import type {
  CreateRoleInput,
  CreateRoleDeleteApprovalInput,
  CreateRoleMetadataEditApprovalInput,
  CreateRolePermissionApprovalInput,
  PermissionFlagsPayload,
  UpdateRoleInput,
  UpdateRoleMetadataInput
} from '@/userManagement/application/types/UserManagementApiContracts';
import type { Permission } from '@/userManagement/domain/entities/Permission';
import type { PermissionRole, RoleDeviation } from '@/userManagement/domain/entities/PermissionRole';

import { sanitizeSieveTermValue } from '@/userManagement/domain/utils/sieveFilterValue';
import { parseFilenameFromContentDisposition } from '@/userManagement/application/utils/triggerBlobDownload';

import { parseSieveListPayload } from './pagedWire';
import { normalizeRolePermissionTemplate } from './rolePermissionsWire';

function asPermissionRole(r: unknown): PermissionRole {
  return r as PermissionRole;
}

function permissionFlagsToEntryInputs(permissions: PermissionFlagsPayload[]) {
  return permissions.map((p) => ({
    featureId: p.featureId,
    allowRead: p.allowRead,
    allowWrite: p.allowWrite,
    allowDelete: p.allowDelete,
    allowCentralAccess: p.allowCentralAccess,
    allowExcelExport: p.allowExcelExport
  }));
}

function mapRoleDeviationWireRow(row: Record<string, unknown>): RoleDeviation {
  return {
    userId: String(row.userId ?? row.UserId ?? row.crossUserId ?? ''),
    username: String(row.username ?? row.Username ?? ''),
    fullName: String(row.fullName ?? row.FullName ?? row.displayName ?? row.DisplayName ?? ''),
    deviationCount: Number(row.deviationCount ?? row.DeviationCount ?? 0)
  };
}

export const userManagementRolesSegment = {
  /**
   * Server-side name filter via Sieve `Filters` (`Name@=…`) and optional `PageSize` override.
   * OpenAPI lists Filters/Sorts/Page/PageSize for GET /roles. Callers that need the full catalog
   * (audit ID lookup, role pickers) should pass `pageSize` large enough to cover any tenant's
   * role count — without it the server returns its default page only.
   */
  listRoles: async (params?: { name?: string; pageSize?: number }): Promise<PermissionRole[]> => {
    const term = params?.name?.trim();
    const query: GetApiUserManagementV1RolesParams = {};
    if (term != null && term.length > 0) {
      query.Filters = `Name@=${sanitizeSieveTermValue(term)}`;
    }
    if (params?.pageSize != null && params.pageSize > 0) {
      query.PageSize = params.pageSize;
    }
    const r = await getApiUserManagementV1Roles(Object.keys(query).length > 0 ? query : undefined);
    const { rows } = parseSieveListPayload<PermissionRole>(r.data, r.headers, 0);
    if (rows.length > 0) return rows;
    const raw = r.data as unknown;
    return Array.isArray(raw) ? (raw as PermissionRole[]) : [];
  },

  getRole: async (id: number): Promise<PermissionRole> => {
    const roleRes = await getApiUserManagementV1RolesRoleId(id);
    const role = asPermissionRole(roleRes.data);
    const normalized = normalizeRolePermissionTemplate(roleRes.data);
    if (normalized !== undefined) {
      role.permissionTemplate = normalized;
    }
    return role;
  },

  /** Role permission matrix rows — GET `/api/user-management/v1/roles/{roleId}/permissions`. */
  getRolePermissions: async (id: number): Promise<Permission[]> => {
    const r = await axiosInstance({ url: `/api/user-management/v1/roles/${id}/permissions`, method: 'GET' });
    const { rows } = parseSieveListPayload<Record<string, unknown>>(r.data, r.headers, 0);
    const raw = rows.length > 0 ? rows : Array.isArray(r.data) ? (r.data as Record<string, unknown>[]) : [];
    return raw.map((row) => mapPermissionWireRowToPermission(row)).filter((p): p is Permission => p !== null);
  },

  /**
   * Role permission matrix export — GET `/api/user-management/v1/roles/{roleId}/permissions/export`.
   * Returns the file blob and a download filename (from Content-Disposition when present).
   */
  exportRolePermissions: async (roleId: number, format: 'csv' | 'xlsx'): Promise<{ blob: Blob; filename: string }> => {
    const r = await axiosInstance<Blob>({
      url: `/api/user-management/v1/roles/${roleId}/permissions/export`,
      method: 'GET',
      params: { format },
      responseType: 'blob'
    });

    const disposition =
      (r.headers['content-disposition'] as string | undefined) ?? (r.headers['Content-Disposition'] as string | undefined);
    const fromHeader = parseFilenameFromContentDisposition(disposition);
    const filename = fromHeader ?? `role-permissions-${roleId}.${format}`;

    return { blob: r.data, filename };
  },

  createRole: async (data: CreateRoleInput, idempotencyKey: string): Promise<PermissionRole> => {
    const body: CreatePermissionRoleInput = {
      name: data.name,
      description: data.description,
      cloneFromId: data.clonedFromId
    };
    const r = await postApiUserManagementV1Roles(body, {
      headers: { 'X-Idempotency-Key': idempotencyKey }
    });
    return asPermissionRole(r.data);
  },

  updateRole: async (id: number, data: UpdateRoleInput): Promise<PermissionRole> => {
    const body = {
      name: data.name,
      description: data.description,
      permissions: permissionFlagsToEntryInputs(data.permissions)
    };
    await axiosInstance({ url: `/api/user-management/v1/roles/${id}`, method: 'PUT', data: body });
    const refreshed = await getApiUserManagementV1RolesRoleId(id);
    return asPermissionRole(refreshed.data);
  },

  /** TROC-227: direct metadata save — name + description only; permissions omitted. */
  updateRoleMetadata: async (id: number, data: UpdateRoleMetadataInput): Promise<PermissionRole> => {
    const body = {
      name: data.name,
      description: data.description
    };
    await axiosInstance({ url: `/api/user-management/v1/roles/${id}`, method: 'PUT', data: body });
    const refreshed = await getApiUserManagementV1RolesRoleId(id);
    return asPermissionRole(refreshed.data);
  },

  /**
   * Queues a role template change for approval — POST `/api/user-management/v1/roles/{roleId}/permissions/approval-requests`.
   * Response: `ApprovalRequestListItemDto` (200); approvals list refreshes from `GET /approval-requests`.
   *
   * TROC-116: `removedFeatureIds` is the explicit-remove counterpart to `permissions` (patch entries).
   * Always sent as an array (defaults to `[]`) so the BE never needs to handle a missing field.
   */
  createRolePermissionApprovalRequest: async (
    roleId: number,
    data: CreateRolePermissionApprovalInput,
    idempotencyKey: string
  ): Promise<unknown> => {
    const r = await axiosInstance<unknown>({
      url: `/api/user-management/v1/roles/${roleId}/permissions/approval-requests`,
      method: 'POST',
      data: {
        roleId,
        justification: data.justification,
        permissions: permissionFlagsToEntryInputs(data.permissions),
        removedFeatureIds: data.removedFeatureIds ?? []
      },
      headers: { 'X-Idempotency-Key': idempotencyKey }
    });
    return r.data;
  },

  /** TROC-175: metadata edit via approval — POST `/roles/{roleId}/edit-approval-requests`. */
  createRoleMetadataEditApprovalRequest: async (
    roleId: number,
    data: CreateRoleMetadataEditApprovalInput,
    idempotencyKey: string
  ): Promise<unknown> => {
    const r = await axiosInstance<unknown>({
      url: `/api/user-management/v1/roles/${roleId}/edit-approval-requests`,
      method: 'POST',
      data: {
        newName: data.newName,
        newDescription: data.newDescription,
        justification: data.justification
      },
      headers: { 'X-Idempotency-Key': idempotencyKey }
    });
    return r.data;
  },

  /** TROC-175: delete via approval — POST `/roles/{roleId}/delete-approval-requests`. */
  createRoleDeleteApprovalRequest: async (
    roleId: number,
    data: CreateRoleDeleteApprovalInput,
    idempotencyKey: string
  ): Promise<unknown> => {
    const r = await axiosInstance<unknown>({
      url: `/api/user-management/v1/roles/${roleId}/delete-approval-requests`,
      method: 'POST',
      data: { justification: data.justification },
      headers: { 'X-Idempotency-Key': idempotencyKey }
    });
    return r.data;
  },

  /** @deprecated TROC-175 — direct delete removed; use createRoleDeleteApprovalRequest. */
  deleteRole: (id: number): Promise<void> =>
    axiosInstance({ url: `/api/user-management/v1/roles/${id}`, method: 'DELETE' }).then(() => undefined),

  /** Users deviating from role template — GET `/api/user-management/v1/roles/{roleId}/deviations`. */
  getRoleDeviations: async (id: number): Promise<RoleDeviation[]> => {
    const r = await getApiUserManagementV1RolesRoleIdDeviations(id);
    const { rows } = parseSieveListPayload<Record<string, unknown>>(r.data, r.headers, 0);
    const raw = rows.length > 0 ? rows : Array.isArray(r.data) ? (r.data as Record<string, unknown>[]) : [];
    return raw.map(mapRoleDeviationWireRow);
  }
};
